import { captureException } from '@tunasong/models'
import { Component } from 'react'
import type { ComponentType, ErrorInfo, FC, ReactNode } from 'react'
import { BlockErrorCard } from './block-error-card.js'
import type { BlockErrorBoundaryOptions, NodeProps } from './block-error-card.js'

export type { BlockErrorBoundaryOptions } from './block-error-card.js'

interface BlockErrorBoundaryProps extends BlockErrorBoundaryOptions {
  nodeProps: NodeProps
  children: ReactNode
}

interface BlockErrorBoundaryState {
  error: Error | null
}

/**
 * One block's error boundary in the editor (GH #2809): a block that throws while rendering is replaced by a card, and
 * the rest of the document keeps rendering and stays editable.
 *
 * A class because React has no hook for catching a render error: `getDerivedStateFromError` and `componentDidCatch`
 * exist only on class components, and the repo has no `react-error-boundary`.
 */
class BlockErrorBoundary extends Component<BlockErrorBoundaryProps, BlockErrorBoundaryState> {
  state: BlockErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): BlockErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    const { element } = this.props.nodeProps
    /** The block type and element id only: no element JSON, no document content */
    void captureException(error, {
      tags: { blockType: element?.type ?? 'unknown', elementId: element?.id ?? 'unknown' },
    })
  }

  handleRetry = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    if (!error) {
      return this.props.children
    }
    const { nodeProps, label, inline } = this.props
    return (
      <BlockErrorCard error={error} nodeProps={nodeProps} label={label} inline={inline} onRetry={this.handleRetry} />
    )
  }
}

/** Wrap a plugin's node component in the block error boundary */
export const withBlockErrorBoundary = <P extends NodeProps>(
  NodeComponent: ComponentType<P>,
  options: BlockErrorBoundaryOptions
) => {
  const Wrapped: FC<P> = props => (
    <BlockErrorBoundary nodeProps={props} label={options.label} inline={options.inline}>
      <NodeComponent {...props} />
    </BlockErrorBoundary>
  )
  Wrapped.displayName = `WithBlockErrorBoundary(${NodeComponent.displayName ?? NodeComponent.name ?? 'Block'})`
  return Wrapped
}
